import { cva, cx } from 'class-variance-authority';

const orientation = {
  horizontal: 'min-w-fit items-center',
  vertical: 'w-full min-w-0 flex-col items-stretch',
} as const;

const side = {
  bottom: '',
  left: '',
  right: '',
} as const;

const tabListBaseVariants = cx('relative flex');

const tabButtonBaseVariants = cx(
  'flex items-center font-normal',
  'cursor-pointer transition-colors duration-150',
  'button-active:cursor-default',
  'button-disabled:cursor-not-allowed',
);

export const tabListVariants = cva([tabListBaseVariants, 'border-neutral-200'], {
  variants: {
    orientation,
    side,
  },
  defaultVariants: {
    orientation: 'horizontal',
    side: 'bottom',
  },
  compoundVariants: [
    { orientation: 'horizontal', side: 'bottom', class: 'border-b' },
    { orientation: 'vertical', side: 'left', class: 'border-l' },
    { orientation: 'vertical', side: 'right', class: 'border-r' },
  ],
});

export const tabPanelVariants = cx('flex-auto not-data-active:hidden');

export const tabButtonVariants = cva(
  [
    tabButtonBaseVariants,
    'group/tab-button px-2 text-neutral-600',
    'button-hover:bg-neutral-50',
    'button-active:text-secondary-900',
    'button-disabled:text-neutral-950/30',
  ],
  {
    variants: {
      orientation: {
        horizontal: 'h-7 min-h-7 gap-2 label-xs',
        vertical:
          'h-auto min-h-14 w-full min-w-0 flex-col justify-center gap-0.5 py-2 text-center label-2xs',
      },
    },
    defaultVariants: {
      orientation: 'horizontal',
    },
  },
);

export const slidingBarVariants = cva(
  'pointer-events-none absolute bg-secondary-900 duration-400 ease-out-smooth',
  {
    variants: {
      orientation: {
        horizontal: 'h-0.5 transition-[left,width]',
        vertical: 'w-0.5 transition-[top,height]',
      },
      side,
    },
    defaultVariants: {
      orientation: 'horizontal',
      side: 'bottom',
    },
    compoundVariants: [
      { orientation: 'horizontal', side: 'bottom', class: 'bottom-0' },
      { orientation: 'vertical', side: 'left', class: 'left-0' },
      { orientation: 'vertical', side: 'right', class: 'right-0' },
    ],
  },
);

export const tabBadgeVariants = cx(
  'group-button-active/tab-button:bg-secondary-100',
  'group-button-active/tab-button:text-secondary-950',
  'group-button-disabled/tab-button:bg-neutral-950/5',
  'group-button-disabled/tab-button:text-neutral-950/30',
);

export const stepTabListVariants = cx(tabListBaseVariants, 'items-stretch');

export const stepTabButtonVariants = cx(
  tabButtonBaseVariants,
  'group/step-tab-button min-h-7 shrink-0 gap-2 py-2 text-left label-xs',
);

export const stepTabMarkerVariants = cva(
  [
    'flex size-7 shrink-0 items-center justify-center rounded-full border',
    'font-medium transition-colors duration-150',
    'group-button-active/step-tab-button:text-white',
    'group-button-disabled/step-tab-button:text-neutral-950/30',
    'group-button-disabled/step-tab-button:border-neutral-950/30 group-button-disabled/step-tab-button:bg-neutral-950/5',
  ],
  {
    variants: {
      status: {
        pending: [
          'border-neutral-500 bg-white text-neutral-500',
          'group-button-active/step-tab-button:border-primary-600',
          'group-button-active/step-tab-button:bg-primary-600',
        ],
        success: [
          'border-success-100 bg-success-100 text-success-600',
          'group-button-active/step-tab-button:border-success-600',
          'group-button-active/step-tab-button:bg-success-600',
        ],
        error: [
          'border-danger-100 bg-danger-100 text-danger-600',
          'group-button-active/step-tab-button:border-danger-600',
          'group-button-active/step-tab-button:bg-danger-600',
        ],
      },
    },
    defaultVariants: {
      status: 'pending',
    },
  },
);

export const stepTabLabelVariants = cx(
  'font-normal text-neutral-500 uppercase',
  'group-button-disabled/step-tab-button:text-neutral-950/30',
);

export const stepTabDescriptionVariants = cx(
  'font-semibold text-neutral-500 transition-colors duration-150',
  'group-button-active/step-tab-button:text-neutral-950',
  'group-button-disabled/step-tab-button:text-neutral-950/30',
);

export const stepTabConnectorVariants = cx('mx-2 h-px w-6 shrink-0 self-center bg-neutral-200');

export type TabGroupVariants = {
  orientation: keyof typeof orientation;
  side: keyof typeof side;
};
